import { Controller, useFormContext } from 'react-hook-form';

import useTranslation from '@/utils/hooks/translation/useTranslation';

//Lib
import { hasNotNumber } from '@/lib/regex';
import { timezone as timezoneOptions } from '@/lib/settings/optionsConfiguration/platformSettings/timezone';
import { hear_about_us, primary_goal } from '@/lib/signup/userInfos/marketingOptions';

import FormFieldMessage from '@/components/base/FormFieldMessage';
import Input from '@/components/base/Input';
//Components
import Label from '@/components/base/Label';
import { Select } from '@/components/base/Select/SelectComponent';
import { SelectFilterComponent } from '@/components/base/SelectFilter/SelectFilterComponent';
import RadioImage from '@/components/composite/Radio/RadioImage';
// Types
import { UserInfosFormType } from '@/components/pages/signup/userInfos/UserInfosForm';

const SubStepOneForm = () => {
    const { t } = useTranslation('signup_funnel');

    const {
        register,
        control,
        watch,
        formState: { errors }
    } = useFormContext<UserInfosFormType>();

    return (
        <>
            <div className="grid w-full grid-cols-1 gap-3 xl:grid-cols-3">
                <div className="w-full">
                    <Label required htmlFor="first_name" fontType="input">
                        {t('user_infos.sub_step_1.first_name')}
                    </Label>
                    <Input
                        data-testid="first_name"
                        placeholder={t('user_infos.sub_step_1.first_name')}
                        type="text"
                        id="first_name"
                        name="first_name"
                        error={errors?.first_name?.message}
                        {...register('first_name', {
                            required: t('user_infos.errors.error_first_name_required'),
                            pattern: {
                                value: hasNotNumber,
                                message: t('user_infos.errors.error_no_numbers')
                            }
                        })}
                    />
                </div>
                <div className="w-full">
                    <Label required htmlFor="last_name" fontType="input">
                        {t('user_infos.sub_step_1.last_name')}
                    </Label>
                    <Input
                        data-testid="last_name"
                        placeholder={t('user_infos.sub_step_1.last_name')}
                        type="text"
                        id="last_name"
                        name="last_name"
                        error={errors?.last_name?.message}
                        {...register('last_name', {
                            required: t('user_infos.errors.error_last_name_required'),
                            pattern: {
                                value: hasNotNumber,
                                message: t('user_infos.errors.error_no_numbers')
                            }
                        })}
                    />
                </div>
                <div className="z-1 w-full">
                    <Label required fontType="input">
                        {t('user_infos.sub_step_1.timezone')}
                    </Label>
                    <Controller
                        control={control}
                        name="timezone"
                        rules={{
                            validate: (el) =>
                                el !== undefined || t('user_infos.errors.error_timezone_required')
                        }}
                        render={({ field: { onChange, value } }) => (
                            <SelectFilterComponent
                                idInput={'search_timezone'}
                                className="max-w-full"
                                selected={timezoneOptions.find((item) => item?.value === value)}
                                buttonVariant="lightBorder"
                                setSelected={(el) => {
                                    onChange(el.value);
                                }}
                                required={true}
                                error={errors?.['timezone']?.message}
                                options={timezoneOptions}
                            />
                        )}
                    />
                </div>
            </div>
            <div>
                <Label required htmlFor="primary_goal" fontType="section">
                    {t('user_infos.sub_step_1.primary_goal.label')}
                </Label>
                <fieldset className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {primary_goal(t).map((opt) => (
                        <RadioImage
                            key={opt.value}
                            id={`primary_goal_${opt.value}`}
                            src={opt.src}
                            label={opt.label}
                            value={opt.value}
                            data-testid={`primary_goal_${opt.value}`}
                            checked={watch('primary_goal') === opt.value}
                            {...register('primary_goal', {
                                required: t('user_infos.errors.error_primary_goal_required')
                            })}
                        />
                    ))}
                </fieldset>
                {errors?.primary_goal ? (
                    <FormFieldMessage className="mt-2">
                        {errors.primary_goal.message}
                    </FormFieldMessage>
                ) : null}
            </div>
            <div>
                <Label required fontType="section">
                    {t('user_infos.sub_step_1.hear_about_us.label')}
                </Label>
                <div className="flex w-full flex-col space-y-m xl:flex-row xl:space-x-3 xl:space-y-0">
                    <Controller
                        control={control}
                        name="hear_about_us"
                        rules={{
                            validate: (el) =>
                                el !== null || t('user_infos.errors.error_hear_about_us_required')
                        }}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                className="w-full"
                                selected={
                                    hear_about_us(t).find((item) => item.value === value) ?? {
                                        label: t('user_infos.sub_step_1.hear_about_us.placeholder'),
                                        value: null
                                    }
                                }
                                buttonVariant="lightBorder"
                                setSelected={(el) => {
                                    onChange(el.value);
                                }}
                                required={true}
                                error={errors?.hear_about_us?.message}
                                options={hear_about_us(t)}
                                panelOptions={{ placement: 'top' }}
                            />
                        )}
                    />
                    <div className="w-full">
                        {['podcast', 'blog', 'other'].includes(watch('hear_about_us')) ? (
                            <Input
                                data-testid="hear_about_us_detail"
                                placeholder={t('user_infos.sub_step_1.hear_about_us.specify')}
                                type="text"
                                id="hear_about_us_detail"
                                name="hear_about_us_detail"
                                error={errors?.hear_about_us_detail?.message}
                                {...register('hear_about_us_detail')}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubStepOneForm;
